/**
 * A tiny typed event emitter. We ship our own rather than depend on Node's
 * `events` module so the bundle stays browser-native and dependency-free (beyond
 * the bundled JsSIP). `Events` maps each event name to its payload type; `void`
 * marks a payload-less event.
 */
export type Listener<T> = (payload: T) => void;

export class Emitter<Events extends object> {
  private handlers: { [K in keyof Events]?: Set<Listener<Events[K]>> } = {};

  on<K extends keyof Events>(event: K, fn: Listener<Events[K]>): this {
    (this.handlers[event] ??= new Set<Listener<Events[K]>>()).add(fn);
    return this;
  }

  once<K extends keyof Events>(event: K, fn: Listener<Events[K]>): this {
    const wrapped: Listener<Events[K]> = (payload) => {
      this.off(event, wrapped);
      fn(payload);
    };
    return this.on(event, wrapped);
  }

  off<K extends keyof Events>(event: K, fn: Listener<Events[K]>): this {
    this.handlers[event]?.delete(fn);
    return this;
  }

  protected emit<K extends keyof Events>(event: K, payload?: Events[K]): void {
    // Copy before iterating so a listener that calls off()/once() mid-dispatch
    // can't mutate the set we're walking. A throwing listener must not take down
    // the SDK or starve the other listeners.
    const listeners = this.handlers[event];
    if (!listeners) return;
    for (const fn of [...listeners]) {
      try {
        fn(payload as Events[K]);
      } catch (err) {
        console.error('[voice-sdk] event listener threw', err);
      }
    }
  }
}
